import { Container, Sprite, Text } from "pixi.js"

import { TextRenderer } from "../text/text_renderer"
import { PACK_SLOT } from "./fit_item_icon"
import { flashTexture } from "../effects/transient_lights"
import { HUD_PANEL_FILL, HUD_PANEL_TEXT, UI_THEMES } from "./ui_tokens"

/**
 * The running count beside the player while they gather: "+3" and the item, one line per kind.
 *
 * It replaced a "+1" that floated off the head for every single pickup -- green, drifting 75px at a
 * constant speed, one label per item, so a heap of five drew five overlapping labels, each carrying
 * a second copy of an item the player had just watched fly into their chest (PickupFlights). Now a
 * pickup only adds to a count: the line appears where the item went in, ticks up with a small warm
 * bump each time another lands, holds while the gathering goes on and fades a moment after it stops.
 * The flight says WHAT; this says HOW MANY, quietly.
 *
 * ONLY THE LOCAL PLAYER gets one (EntityRenderingManager.itemPickedUp). Everyone nearby still sees
 * items fly into you; nobody needs your arithmetic.
 *
 * One of these is parented to the player's world-overlay item (see WorldOverlaySurface), like the
 * hold dial, so it follows the body with no position arithmetic of its own, and is swept once a
 * frame by WorldOverlaySurface.animatePickupTallies until its last line has faded.
 */

export const PICKUP_TALLY = {
    /** How long a line stays after its last pickup, then how long it takes to go. */
    holdMs: 700,
    fadeMs: 350,
    /** A new line swelling in, and a count going up. */
    appearMs: 200,
    bumpMs: 180,
    bump: 0.3,
    lineGap: 28,
    maxLines: 3,
}

// Geometry in world-overlay pixels (screen pixels at the default zoom; see HoldTimer). The overlay
// item is parked half a tile LEFT of the entity and a tile and a quarter ABOVE its location, so the
// body's centre line is x=16. Measured on the default Ember build: the arms hang to about x=41 and the
// upper chest -- where PickupFlights lands an item -- is at y=46. The count starts just clear of the
// arm, level with where the item went in.
const ORIGIN_X = 44
const ORIGIN_Y = 46
// The item at the size the pack shows it (fitItemIcon in a PACK_SLOT box), the size the player knows it
// by. An 18px box was tried first and its icons were too small to make out.
export const TALLY_ICON = PACK_SLOT
// The label's box carries its outline's padding, so the icon tucks back into it a little.
const ICON_GAP = -1
const FONT_SIZE = 16
// A soft warm pool under the icon -- the landing glint's colour, faint. Without it a dark item (coal,
// a log) is a hole in dark ground after nightfall and the line reads as a bare number. The glint's own
// gaussian falloff, not stacked circles: at pack size those showed their steps as rings at dusk.
const BACKING_SIZE = TALLY_ICON * 1.4
const BACKING_ALPHA = 0.3

const THEME = UI_THEMES.ember

interface Line {
    key: string
    count: number
    row: Container
    label: Text
    bornMs: number
    lastMs: number
    bumpMs: number
    y: number
}

const easeOut = (t: number) => 1 - (1 - t) * (1 - t)
// Overshoots a touch and settles: the line arriving, rather than switching on.
const easeOutBack = (t: number) => {
    const c = 1.6
    return 1 + (c + 1) * Math.pow(t - 1, 3) + c * Math.pow(t - 1, 2)
}
const mix = (a: number, b: number, t: number) => {
    const ch = (shift: number) => Math.round(((a >> shift) & 0xff) * (1 - t) + ((b >> shift) & 0xff) * t)
    return (ch(16) << 16) | (ch(8) << 8) | ch(0)
}

export class PickupTally extends Container {
    private lines: Line[] = []

    constructor() {
        super()
        this.position.set(ORIGIN_X, ORIGIN_Y)
        this.interactiveChildren = false
    }

    /**
     * Count `quantity` more of `key` (one line per key). `icon` builds the item's icon, about
     * TALLY_ICON square with its top-left at the origin, the first time a line for it is drawn.
     */
    add = (key: string, quantity: number, icon: () => Container | undefined, nowMs: number = performance.now()) => {
        const line = this.lines.find(l => l.key === key)
        if (line) {
            line.count += quantity
            line.label.text = `+${line.count}`
            line.lastMs = nowMs
            line.bumpMs = nowMs
            this.layoutRow(line)
            return
        }
        // Past the cap the oldest kind starts going now, making room for the new one.
        const live = this.lines.filter(l => !this.fading(l, nowMs))
        if (live.length >= PICKUP_TALLY.maxLines) live[0].lastMs = Math.min(live[0].lastMs, nowMs - PICKUP_TALLY.holdMs)

        const label = new TextRenderer().fontSize(FONT_SIZE).bold().strokeThickness(3).color(HUD_PANEL_TEXT).render(`+${quantity}`) as Text
        label.style.stroke = HUD_PANEL_FILL
        label.style.lineJoin = "round"
        label.anchor.set(0, 0.5)
        // Pinned so it is not re-rasterized -- and does not glitch for a frame -- in the follow-cam PiP.
        label.resolution = 2 * (typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1)
        const row = new Container()
        row.addChild(label)
        const art = icon()
        if (art) {
            const backing = new Sprite(flashTexture())
            backing.anchor.set(0.5)
            backing.position.set(TALLY_ICON / 2, TALLY_ICON / 2)
            backing.width = backing.height = BACKING_SIZE
            backing.tint = THEME.accent
            backing.alpha = BACKING_ALPHA
            const holder = new Container()
            holder.name = "icon"
            holder.addChild(backing, art)
            row.addChild(holder)
        }
        this.addChild(row)
        const fresh: Line = { key, count: quantity, row, label, bornMs: nowMs, lastMs: nowMs, bumpMs: -Infinity, y: 0 }
        this.layoutRow(fresh)
        this.lines.push(fresh)
        fresh.y = this.slotY(this.lines.length - 1)
    }

    /** Advance every line; false once the last one has gone, when the owner drops this. */
    animate = (nowMs: number = performance.now()): boolean => {
        const { holdMs, fadeMs, appearMs, bumpMs, bump } = PICKUP_TALLY
        for (let i = this.lines.length - 1; i >= 0; i--) {
            const line = this.lines[i]
            const fade = (nowMs - line.lastMs - holdMs) / fadeMs
            if (fade >= 1) {
                line.row.destroy({ children: true })
                this.lines.splice(i, 1)
            }
        }
        this.lines.forEach((line, i) => {
            // Lines settle into their slots rather than jumping when one below them goes.
            const target = this.slotY(i)
            line.y += (target - line.y) * 0.25
            if (Math.abs(target - line.y) < 0.1) line.y = target

            const age = nowMs - line.bornMs
            const appear = Math.min(1, Math.max(0, age / appearMs))
            const fade = Math.min(1, Math.max(0, (nowMs - line.lastMs - holdMs) / fadeMs))
            const sinceBump = (nowMs - line.bumpMs) / bumpMs
            const bumping = sinceBump >= 0 && sinceBump < 1
            const swell = bumping ? bump * (1 - easeOut(sinceBump)) : 0

            line.row.alpha = Math.min(1, age / (appearMs * 0.45)) * (1 - fade)
            line.row.scale.set((0.6 + 0.4 * easeOutBack(appear)) * (1 + swell))
            line.row.position.set(0, line.y + 6 * (1 - easeOut(appear)) - 8 * easeOut(fade))
            // A count going up flares to the ember accent and cools back to the panel text.
            line.label.tint = bumping ? mix(THEME.accent, HUD_PANEL_TEXT, easeOut(sinceBump)) : HUD_PANEL_TEXT
        })
        return this.lines.length > 0
    }

    // Exposed for tests.
    lineCounts = (): { key: string; count: number }[] => this.lines.map(l => ({ key: l.key, count: l.count }))

    private fading = (line: Line, nowMs: number) => nowMs - line.lastMs > PICKUP_TALLY.holdMs

    // Newest kind on top: the stack grows upward from where the items go in.
    private slotY = (i: number) => -i * PICKUP_TALLY.lineGap

    // Number then icon, the row's pivot at its left middle so a bump swells away from the body.
    private layoutRow = (line: Line) => {
        const art = line.row.getChildByName("icon") as Container | null
        if (art) art.position.set(line.label.width + ICON_GAP, -TALLY_ICON / 2)
    }
}
